import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spin, Empty, Typography } from "antd";
import { useAllMerchants } from "./hooks/useAllMerchants";
import { useLiveTransactions } from "./hooks/useLiveTransactions";
import TransactionMap from "./components/TransactionMap";
import TransactionCounter from "./components/TransactionCounter";
import { Transaction } from "./liveTransactionMapApi";
import styles from "./LiveTransactionMap.module.css";

const { Title } = Typography;
const ONE_BILLION = 1_000_000_000;
const CONFETTI_BURST_DURATION_MS = 7000;

/**
 * Main Live Transaction Map component
 * Displays merchants on a map with animated transaction particles
 */
export default function LiveTransactionMap() {
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasPlayedConfetti, setHasPlayedConfetti] = useState(false);
  const {
    merchants,
    loading: merchantsLoading,
    error: merchantsError,
    refetch: refetchMerchants,
  } = useAllMerchants();

  const {
    transactions,
    newTransactions,
    loading: transactionsLoading,
    error: transactionsError,
    summary,
    runningLifetimeTpv,
    refetch: refetchTransactions,
    pause,
    resume,
    isPaused,
  } = useLiveTransactions({
    enabled: true, // Will be controlled by isPaused state
    pollInterval: 10000, // 10 seconds
    limit: 100,
  });

  // Track which transactions have been animated (for future use if needed)
  const handleTransactionAnimate = useCallback((transaction: Transaction) => {
    // Could track animated transactions here if needed for analytics
    // For now, just a placeholder callback
    if (transaction && transaction._id) {
      // Transaction animation started
    }
  }, []);

  const handleTransactionComplete = useCallback((transactionId: string) => {
    // Animation completed - cleanup handled by TransactionMap component
    if (transactionId) {
      // Transaction animation completed
    }
  }, []);

  useEffect(() => {
    if (hasPlayedConfetti || runningLifetimeTpv < ONE_BILLION) {
      return;
    }

    setShowConfetti(true);
    setHasPlayedConfetti(true);

    const timeoutId = window.setTimeout(() => {
      setShowConfetti(false);
    }, CONFETTI_BURST_DURATION_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [runningLifetimeTpv, hasPlayedConfetti]);

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 90 }).map((_, index) => ({
        id: index,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 2.5}s`,
        duration: `${2.8 + Math.random() * 2.4}s`,
      })),
    []
  );

  // Show loading state while merchants are loading
  if (merchantsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spin size="large" />
          <Title level={4} style={{ marginTop: 16 }}>
            Loading merchants...
          </Title>
        </div>
      </div>
    );
  }

  // Show error state
  if (merchantsError) {
    return (
      <div className={styles.container}>
        <Alert
          message="Error Loading Merchants"
          description={merchantsError}
          type="error"
          showIcon
          action={
            <button onClick={refetchMerchants} className={styles.retryButton}>
              Retry
            </button>
          }
        />
      </div>
    );
  }

  // Show empty state if no merchants
  if (merchants.size === 0) {
    return (
      <div className={styles.container}>
        <Empty
          description="No merchants with valid coordinates found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title level={2}>Live Transaction Map</Title>
        <div className={styles.subtitle}>
          Real-time visualization of transactions across all merchants
        </div>
      </div>

      {transactionsError && (
        <Alert
          message="Error Loading Transactions"
          description={transactionsError}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <div className={styles.mapWrapper}>
        {showConfetti && (
          <div className={styles.confettiLayer} aria-hidden="true">
            {confettiPieces.map((piece) => (
              <span
                key={piece.id}
                className={styles.confettiPiece}
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                }}
              />
            ))}
          </div>
        )}

        <TransactionMap
          merchants={merchants}
          transactions={transactions}
          newTransactions={newTransactions}
          onTransactionAnimate={handleTransactionAnimate}
          onTransactionComplete={handleTransactionComplete}
        />

        <TransactionCounter
          totalTransactions={summary?.totalTransactions || 0}
          totalAmount={summary?.totalAmount || 0}
          yoyTpv={summary?.yoyTpv || null}
          lifetimeTpv={runningLifetimeTpv}
          startDate={summary?.startDate || null}
          endDate={summary?.endDate || null}
          loading={transactionsLoading}
          isPaused={isPaused}
          onRefresh={refetchTransactions}
          onPause={pause}
          onResume={resume}
        />
      </div>
    </div>
  );
}
